import React from "react";
import { AlertTriangle, CheckCircle2, Loader2, OctagonAlert, RefreshCw } from "lucide-react";

const TONE = {
    ok: { box: "border-emerald-300/30 bg-emerald-300/[0.05]", text: "text-emerald-100", label: "All keys healthy", Icon: CheckCircle2 },
    warning: { box: "border-amber-300/40 bg-amber-300/[0.06]", text: "text-amber-100", label: "Needs attention", Icon: AlertTriangle },
    error: { box: "border-rose-400/40 bg-rose-400/[0.07]", text: "text-rose-100", label: "Not working", Icon: OctagonAlert },
};

const BUILTIN_TEXT = {
    ok: "set · Emergent key",
    missing: "not set",
    not_emergent: "set · not an Emergent key",
    failing: "set · calls failing",
};

const SAVED_TEXT = {
    ok: "valid",
    not_saved: "not saved",
    rejected: "rejected by provider",
    unreadable: "can't be decrypted",
    unreachable: "couldn't be checked",
    no_model_access: "valid · no access to the app's model",
};

/** LLM Keys page banner — live key checks + failed calls in the last hour. */
export function KeyHealthBanner({ health, loading, onRecheck }) {
    if (!health) {
        return (
            <div className="mt-4 text-[11px] text-white/45 font-mono inline-flex items-center gap-2" data-testid="llm-health-loading">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Checking keys…
            </div>
        );
    }
    const tone = TONE[health.status] || TONE.warning;
    return (
        <section className={`mt-4 border rounded-xl p-4 ${tone.box}`} data-testid="llm-health-banner">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className={`inline-flex items-center gap-2 text-sm font-medium ${tone.text}`} data-testid="llm-health-status" data-status={health.status}>
                    <tone.Icon className="w-4 h-4" /> {tone.label}
                </div>
                <div className="flex items-center gap-3 text-[11px] text-white/45">
                    <span data-testid="llm-health-checked-at">checked {new Date(health.checked_at).toLocaleTimeString()}</span>
                    <button onClick={onRecheck} disabled={loading} data-testid="llm-health-recheck-btn"
                            className="px-2.5 py-1 border border-white/15 rounded-md hover:bg-white/5 inline-flex items-center gap-1.5 disabled:opacity-40 transition-colors">
                        <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Re-check
                    </button>
                </div>
            </div>
            <HealthSummary health={health} />
            {health.issues.length > 0 && (
                <ul className="mt-3 space-y-1.5" data-testid="llm-health-issues">
                    {health.issues.map((i, n) => (
                        <li key={n} data-testid={`llm-health-issue-${n}`} data-severity={i.severity}
                            className={`text-[12px] leading-relaxed flex gap-2 ${i.severity === "error" ? "text-rose-100" : "text-amber-100"}`}>
                            {i.severity === "error" ? <OctagonAlert className="w-3.5 h-3.5 mt-0.5 shrink-0" /> : <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />}
                            <span className="break-words min-w-0">{i.message}</span>
                        </li>
                    ))}
                </ul>
            )}
        </section>
    );
}

function HealthSummary({ health }) {
    const items = [
        ["builtin", "Built-in key (EMERGENT_LLM_KEY)", BUILTIN_TEXT[health.builtin.status] || health.builtin.status],
        ["anthropic", "Your Anthropic key", SAVED_TEXT[health.saved.anthropic?.status] || "—"],
        ["openai", "Your OpenAI key", SAVED_TEXT[health.saved.openai?.status] || "—"],
        ["gemini", "Your Gemini key", SAVED_TEXT[health.saved.gemini?.status] || "—"],
    ];
    return (
        <dl className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 text-[11px]">
            {items.map(([id, label, value]) => (
                <div key={id} className="bg-black/30 border border-white/[0.06] rounded-md px-3 py-2" data-testid={`llm-health-${id}`}>
                    <dt className="text-white/45 uppercase tracking-wider text-[10px]">{label}</dt>
                    <dd className="text-white/85 mt-0.5">{value}</dd>
                </div>
            ))}
        </dl>
    );
}
